import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drum, Wrench, Music, ArrowRight, Youtube } from 'lucide-react';
import VisualizerBackground from '../components/VisualizerBackground';
import ServiceCard from '../components/ServiceCard';
import ScrollAnimation from '../components/ScrollAnimation';
import ParallaxSection from '../components/ParallaxSection';
import OptimizedImage from '../components/OptimizedImage';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Remove the # character
      const id = location.hash.substring(1);
      
      // Find the element
      const element = document.getElementById(id);
      
      // If found, scroll to it after a delay to ensure everything is loaded
      if (element) {
        // Add a larger delay to ensure all animations and layout are complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 500);
      } else {
        // If specific section isn't found, scroll to the detailed services section
        const detailedServices = document.getElementById('detailed-services');
        if (detailedServices) {
          setTimeout(() => {
            detailedServices.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 500);
        }
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-drummer-dark text-white">
      <VisualizerBackground />
      
      {/* Hero */}
      <section className="pt-28 pb-4 md:pt-36 md:pb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollAnimation animation="fadeUp" duration={0.8}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Professional</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">
                  Services
                </span>
              </h1>
              <p className="text-xl text-white">
                Expert solutions delivered with technical excellence and creative precision
              </p>
            </ScrollAnimation>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="py-6 md:py-12 relative">
        <ParallaxSection className="absolute top-20 right-1/4 w-64 h-64 bg-drummer-purple/5 rounded-full blur-3xl -z-10" speed={-0.2}>
          <div className="w-full h-full" />
        </ParallaxSection>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollAnimation animation="fadeUp" delay={0.1} duration={0.6}>
              <ServiceCard 
                title="Session Drumming"
                description="Studio-quality drum performances with precision timing and dynamic expression. Professional recording services for artists, producers, and composers."
                icon={<Drum size={32} />}
                serviceId="session-drummer"
              />
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.3} duration={0.6}>
              <ServiceCard 
                title="Music Production"
                description="Comprehensive production services from arrangement to final mix. Technical expertise and creative direction for your musical projects."
                icon={<Music size={32} />}
                serviceId="music-production"
              />
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.5} duration={0.6}>
              <ServiceCard 
                title="Content Creation"
                description="Professional video and photography with a musician's understanding of timing and composition. Visual storytelling that captures the essence of your brand."
                icon={<Youtube size={32} />}
                serviceId="content-creation"
              />
            </ScrollAnimation>
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="py-8 md:py-16 relative">
        <ParallaxSection className="absolute top-1/3 left-10 w-80 h-80 bg-client-teal/5 rounded-full blur-3xl -z-10" speed={0.3}>
          <div className="w-full h-full" />
        </ParallaxSection>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <ScrollAnimation animation="fadeIn" duration={0.7}>
              <h2 className="text-4xl md:text-5xl font-bold mb-2">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">The Professional Process</span>
              </h2>
              <p className="text-xl text-white">
                A systematic approach that ensures exceptional results for every project
              </p>
            </ScrollAnimation>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-3">
              {/* Step 1 */}
              <ScrollAnimation animation="fadeUp" delay={0.1} duration={0.5}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-b from-client-yellow/20 to-client-yellow/5 rounded-2xl transform group-hover:scale-105 transition-all duration-300 group-hover:from-client-yellow/30 group-hover:to-client-yellow/10"></div>
                  <div className="relative p-5 rounded-2xl bg-client-black/40 backdrop-blur-sm border border-client-yellow/10 group-hover:border-client-yellow/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-client-yellow to-client-yellow/80 flex items-center justify-center text-client-black font-bold text-lg mb-3 shadow-lg shadow-client-yellow/20">
                      1
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-client-yellow/90 transition-colors duration-300">Consultation</h3>
                    <p className="text-white text-sm">
                      In-depth discussion of your project goals, requirements, and creative vision.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
              
              {/* Step 2 */}
              <ScrollAnimation animation="fadeUp" delay={0.2} duration={0.5}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-b from-client-yellow/20 to-client-yellow/5 rounded-2xl transform group-hover:scale-105 transition-all duration-300 group-hover:from-client-yellow/30 group-hover:to-client-yellow/10"></div>
                  <div className="relative p-5 rounded-2xl bg-client-black/40 backdrop-blur-sm border border-client-yellow/10 group-hover:border-client-yellow/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-client-yellow to-client-yellow/80 flex items-center justify-center text-client-black font-bold text-lg mb-3 shadow-lg shadow-client-yellow/20">
                      2
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-client-yellow/90 transition-colors duration-300">Strategy</h3>
                    <p className="text-white text-sm">
                      Development of a tailored approach that aligns with your objectives and timeline.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
              
              {/* Step 3 */}
              <ScrollAnimation animation="fadeUp" delay={0.3} duration={0.5}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-b from-client-yellow/20 to-client-yellow/5 rounded-2xl transform group-hover:scale-105 transition-all duration-300 group-hover:from-client-yellow/30 group-hover:to-client-yellow/10"></div>
                  <div className="relative p-5 rounded-2xl bg-client-black/40 backdrop-blur-sm border border-client-yellow/10 group-hover:border-client-yellow/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-client-yellow to-client-yellow/80 flex items-center justify-center text-client-black font-bold text-lg mb-3 shadow-lg shadow-client-yellow/20">
                      3
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-client-yellow/90 transition-colors duration-300">Execution</h3>
                    <p className="text-white text-sm">
                      Professional implementation with meticulous attention to detail and technical precision.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
              
              {/* Step 4 */}
              <ScrollAnimation animation="fadeUp" delay={0.4} duration={0.5}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-b from-client-yellow/20 to-client-yellow/5 rounded-2xl transform group-hover:scale-105 transition-all duration-300 group-hover:from-client-yellow/30 group-hover:to-client-yellow/10"></div>
                  <div className="relative p-5 rounded-2xl bg-client-black/40 backdrop-blur-sm border border-client-yellow/10 group-hover:border-client-yellow/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-client-yellow to-client-yellow/80 flex items-center justify-center text-client-black font-bold text-lg mb-3 shadow-lg shadow-client-yellow/20">
                      4
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-client-yellow/90 transition-colors duration-300">Refinement</h3>
                    <p className="text-white text-sm">
                      Collaborative review process to ensure the final product exceeds expectations.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
              
              {/* Step 5 */}
              <ScrollAnimation animation="fadeUp" delay={0.5} duration={0.5}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-b from-client-yellow/20 to-client-yellow/5 rounded-2xl transform group-hover:scale-105 transition-all duration-300 group-hover:from-client-yellow/30 group-hover:to-client-yellow/10"></div>
                  <div className="relative p-5 rounded-2xl bg-client-black/40 backdrop-blur-sm border border-client-yellow/10 group-hover:border-client-yellow/20 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-client-yellow to-client-yellow/80 flex items-center justify-center text-client-black font-bold text-lg mb-3 shadow-lg shadow-client-yellow/20">
                      5
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-client-yellow/90 transition-colors duration-300">Delivery</h3>
                    <p className="text-white text-sm">
                      Professional delivery of final assets with comprehensive support and follow-up.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </div>
        </div>
      </section>
      
      {/* Detailed Service Sections */}
      <section className="py-8 md:py-16 relative" id="detailed-services">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <ScrollAnimation animation="fadeIn" duration={0.7}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Our Services in Detail</span>
              </h2>
              <p className="text-xl text-white">
                Explore our comprehensive service offerings designed to meet your creative needs
              </p>
            </ScrollAnimation>
          </div>
          <div className="space-y-20">
            {/* Session Drumming Section */}
            <div id="session-drummer" className="scroll-mt-32 pt-4">
              <ScrollAnimation>
                <div className="glassmorphic p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-12">
                    <div className="order-2 md:order-1">
                      <h3 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Session Drumming</h3>
                      <p className="text-white mb-4">
                        Professional studio-quality drum performances with precision timing and dynamic expression, 
                        perfect for artists, producers, and composers looking to elevate their projects.
                      </p>
                      <ul className="space-y-2 text-white mb-6">
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Studio recording sessions for singles, EPs, and albums</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Remote drum tracking with quick turnaround</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Live performance for events and studio sessions</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Custom drum parts and arrangement consulting</span>
                        </li>
                      </ul>
                      <Link to="/contact?service=session-drummer" className="drum-button">
                        Request Quote
                      </Link>
                    </div>
                    <div className="order-1 md:order-2">
                      <ScrollAnimation animation="slideRight" delay={0.1}>
                        <div className="electric-border">
                          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                            <OptimizedImage
                              src="/photos/webp/sm23-milestone1.webp"
                              alt="Session drumming services"
                              className="w-full h-full object-cover"
                              width={800}
                              height={450}
                            />
                          </div>
                        </div>
                      </ScrollAnimation>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>

            {/* Music Production Section */}
            <div id="music-production" className="scroll-mt-32 pt-4">
              <ScrollAnimation>
                <div className="glassmorphic p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-12">
                    <div>
                      <h3 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Music Production</h3>
                      <p className="text-white mb-4">
                        Comprehensive production services from arrangement to final mix, providing 
                        technical expertise and creative direction for your musical projects.
                      </p>
                      <ul className="space-y-2 text-white mb-6">
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Full production from concept to completion</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Arrangement and composition services</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Mixing and mastering for professional sound</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Beat production and instrumental creation</span>
                        </li>
                      </ul>
                      <Link to="/contact?service=music-production" className="drum-button">
                        Request Quote
                      </Link>
                    </div>
                    <div>
                      <ScrollAnimation animation="slideLeft" delay={0.1}>
                        <div className="electric-border">
                          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                            <OptimizedImage
                              src="/photos/webp/sm23-rockwood5.webp"
                              alt="Music production services"
                              className="w-full h-full object-cover"
                              width={800}
                              height={450}
                            />
                          </div>
                        </div>
                      </ScrollAnimation>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>

            {/* Content Creation Section */}
            <div id="content-creation" className="scroll-mt-32 pt-4">
              <ScrollAnimation>
                <div className="glassmorphic p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-12">
                    <div className="order-2 md:order-1">
                      <h3 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Content Creation</h3>
                      <p className="text-white mb-4">
                        Professional video and photography with a musician's understanding of timing and 
                        composition, creating visual storytelling that captures the essence of your brand.
                      </p>
                      <ul className="space-y-2 text-white mb-6">
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Music videos and performance capture</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Social media content creation and strategy</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Professional photography for artists and brands</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Event videography and documentary production</span>
                        </li>
                      </ul>
                      <Link to="/contact?service=content-creation" className="drum-button">
                        Request Quote
                      </Link>
                    </div>
                    <div className="order-1 md:order-2">
                      <ScrollAnimation animation="slideRight" delay={0.1}>
                        <div className="electric-border">
                          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                            <OptimizedImage
                              src="/photos/webp/sm23-smithbar1.webp"
                              alt="Content creation services"
                              className="w-full h-full object-cover"
                              width={800}
                              height={450}
                            />
                          </div>
                        </div>
                      </ScrollAnimation>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>

            {/* Drum Repair & Tuning Section */}
            <div id="drum-repair" className="scroll-mt-32 pt-4">
              <ScrollAnimation>
                <div className="glassmorphic p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-12">
                    <div>
                      <h3 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Drum Repair & Tuning</h3>
                      <p className="text-white mb-4">
                        Expert drum maintenance, repair, and tuning services to ensure your drums 
                        sound their best and perform reliably for your recordings and performances.
                      </p>
                      <ul className="space-y-2 text-white mb-6">
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Professional drum tuning for optimal sound</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Hardware repair and maintenance</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Shell restoration and refinishing</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-client-yellow mr-2">•</span>
                          <span>Custom drum setup consultation</span>
                        </li>
                      </ul>
                      <Link to="/contact?service=drum-repair" className="drum-button">
                        Request Quote
                      </Link>
                    </div>
                    <div>
                      <ScrollAnimation animation="slideLeft" delay={0.1}>
                        <div className="electric-border">
                          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                            <OptimizedImage
                              src="/photos/webp/sm23-ironcow1.webp"
                              alt="Drum repair and tuning services"
                              className="w-full h-full object-cover"
                              width={800}
                              height={450}
                            />
                          </div>
                        </div>
                      </ScrollAnimation>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <ScrollAnimation animation="scaleUp" duration={0.8}>
            <div className="glassmorphic p-10 md:p-16 lg:p-20">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">Ready to Elevate Your Project?</h2>
                <p className="text-xl text-white mb-10">
                  Partner with a professional who brings technical expertise and creative vision to every collaboration.
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                  <Link to="/contact" className="drum-button">
                    Start a Conversation <ArrowRight size={20} className="ml-2" />
                  </Link>
                  <Link to="/videos" className="drum-button-outline">
                    View Performances
                  </Link>
                </div>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default Services;
